import { ColumnsType } from 'antd/es/table';
import { PrettyCurrency, Typography } from 'components';
import { Payment } from 'gql/graphql';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { useMemo } from 'react';
import { dateTimeShort } from 'utils/time/dateTimeShort';

enum PaymentsKeysEnum {
  Id = 'id',
  Status = 'status',
  Type = 'type',
  Date = 'date',
  Invoice = 'invoice',
  Amount = 'amount'
}

interface IUseTablePaymentsOut {
  columns: ColumnsType<Payment>;
}

export default function useTablePayments(): IUseTablePaymentsOut {
  const t = useTranslations();

  const columns: ColumnsType<Payment> = useMemo(
    () => [
      {
        title: t(`billing.payments.table.${PaymentsKeysEnum.Id}` as any),
        dataIndex: PaymentsKeysEnum.Id,
        key: PaymentsKeysEnum.Id,
        render: (_, payment) => (
          <Typography variant="regular4" className="select-none  text-tertiary-100">
            {payment.id}
          </Typography>
        )
      },
      {
        title: t(`billing.payments.table.${PaymentsKeysEnum.Status}` as any),
        dataIndex: PaymentsKeysEnum.Status,
        key: PaymentsKeysEnum.Status,
        render: (_, payment) => (
          <Typography variant="regular4" className="select-none  text-tertiary-100">
            {t(`billing.payments.status.${payment.status}` as any)}
          </Typography>
        )
      },
      {
        title: t(`billing.payments.table.${PaymentsKeysEnum.Type}` as any),
        dataIndex: PaymentsKeysEnum.Type,
        key: PaymentsKeysEnum.Type,
        render: (_, payment) => (
          <Typography variant="regular4" className="select-none  text-tertiary-100">
            {t(`billing.payments.type.${payment.type}` as any)}
          </Typography>
        )
      },
      {
        title: t(`billing.payments.table.${PaymentsKeysEnum.Date}` as any),
        dataIndex: PaymentsKeysEnum.Date,
        key: PaymentsKeysEnum.Date,
        render: (_, payment) => (
          <Typography variant="regular4" className="select-none  text-tertiary-100">
            {dateTimeShort(payment.createdAt, {
              includeYear: true,
              showYesterday: true,
              t: t as any
            })}
          </Typography>
        )
      },
      {
        title: t(`billing.payments.table.${PaymentsKeysEnum.Invoice}` as any),
        dataIndex: PaymentsKeysEnum.Invoice,
        key: PaymentsKeysEnum.Invoice,
        render: (_, payment) => (
          <>
            {payment.invoice?.id ? (
              <Link href={`/billing/invoices/${payment.invoice?.id}`}>
                <Typography variant="regular4" className="select-none text-secondary-link">
                  #{payment.invoice?.id}
                </Typography>
              </Link>
            ) : (
              <Typography variant="regular4" className="select-none text-tertiary-200">
                {t('billing.payments.n_a')}
              </Typography>
            )}
          </>
        )
      },
      {
        title: t(`billing.payments.table.${PaymentsKeysEnum.Amount}` as any),
        dataIndex: PaymentsKeysEnum.Amount,
        key: PaymentsKeysEnum.Amount,
        render: (_, purchaseOrder) => (
          <Typography variant="regular4" className="select-none text-tertiary-100">
            <PrettyCurrency
              className="!font-normal"
              amount={purchaseOrder.amountInCents / 100}
              currency={purchaseOrder.currency}
            />
          </Typography>
        )
      }
    ],
    [t]
  );

  return {
    columns
  };
}
